'use client';

import { useCallback, useEffect, useId, useRef } from 'react';
import { cn } from '@ncai-axe/react-ui-v3';
import styles from './modalContainer.module.scss';
import { ModalTitleIdProvider } from './ModalTitleContext';
import { ModalContainerProps } from './types';

const DRAG_THRESHOLD_PX = 5;

const FOCUSABLE_SELECTOR = [
  'a[href]:not([disabled]):not([tabindex="-1"])',
  'button:not([disabled]):not([tabindex="-1"])',
  'textarea:not([disabled]):not([tabindex="-1"])',
  'input:not([disabled]):not([tabindex="-1"])',
  'select:not([disabled]):not([tabindex="-1"])',
  '[tabindex]:not([tabindex="-1"]):not([disabled])',
].join(', ');

export function ModalContainer({
  minHeight,
  children,
  className,
  onClickContent,
  onClickBackdrop,
  ...props
}: ModalContainerProps) {
  const mouseDownRef = useRef<{ x: number; y: number } | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== 'Tab') return;

    const dialog = dialogRef.current;
    if (!dialog) return;

    const focusableElements =
      dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    if (focusableElements.length === 0) {
      e.preventDefault();
      return;
    }

    const first = focusableElements[0];
    const last = focusableElements[focusableElements.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }, []);

  const handleMouseDownBackdrop = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      mouseDownRef.current = { x: e.clientX, y: e.clientY };
    }
  };

  const handleClickBackdrop = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;

    const mouseDown = mouseDownRef.current;
    mouseDownRef.current = null;
    if (!mouseDown) return;

    const deltaX = Math.abs(e.clientX - mouseDown.x);
    const deltaY = Math.abs(e.clientY - mouseDown.y);
    if (deltaX > DRAG_THRESHOLD_PX || deltaY > DRAG_THRESHOLD_PX) return;

    onClickBackdrop?.(e);
  };

  const handleClickContent = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    onClickContent?.(e);
  };

  return (
    <div
      className={styles.backdrop}
      onMouseDown={handleMouseDownBackdrop}
      onClick={handleClickBackdrop}
    >
      <div
        ref={dialogRef}
        className={cn(styles.modal, className)}
        style={{ minHeight }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        onClick={handleClickContent}
        {...props}
      >
        <div className={styles.modalInner} onClick={(e) => e.stopPropagation()}>
          <ModalTitleIdProvider value={titleId}>
            {children}
          </ModalTitleIdProvider>
        </div>
      </div>
    </div>
  );
}

ModalContainer.displayName = 'ModalContainer';
